import { ROUTE_PERMISSIONS } from '../../../core/constants/route-permissions.constant';

export const AUTOMATION_GUIDE_ID = 'automation';

function link(label, state, params) {
  return { label, state, params, permission: ROUTE_PERMISSIONS[state] };
}

const AGENTS = link('Abrir Agente de IA', 'app.ai-agents');
const CHATBOTS = link('Abrir Chatbots', 'app.chatbots');
const AUTOMATIONS = link('Abrir Automações', 'app.automation');
const CAMPAIGNS = link('Abrir Campanhas', 'app.campaigns');
const TEMPLATES = link('Abrir Templates', 'app.templates');
const QUICK_REPLIES = link('Abrir Respostas Rápidas', 'app.quick-replies');

export const AGENT_INSTRUCTION_EXAMPLE =
  'Você é um assistente de atendimento da empresa. Responda de forma clara, educada e objetiva, utilizando apenas as informações fornecidas e os recursos autorizados. Quando não souber uma resposta ou precisar de intervenção humana, não invente informações e siga o procedimento de encaminhamento configurado.';

export function visibleGuideSteps(hasPermission) {
  const allowed = (permission) => !permission || hasPermission(permission);
  return AUTOMATION_GUIDE_STEPS.filter((step) => allowed(step.permission)).map((step) => ({
    ...step,
    blocks: step.blocks
      ? step.blocks
          .map((block) => ({
            ...block,
            items: block.items
              .filter((item) => typeof item === 'string' || allowed(item.permission))
              .map((item) => (typeof item === 'string' ? item : item.text))
          }))
          .filter((block) => block.items.length)
      : step.blocks,
    comparison: step.comparison
      ? step.comparison.filter((card) => allowed(card.link && card.link.permission))
      : step.comparison,
    links: step.links ? step.links.filter((item) => allowed(item.permission)) : step.links
  }));
}

export const AUTOMATION_GUIDE_STEPS = [
  {
    id: 'overview',
    short: 'Visão geral',
    icon: 'grid',
    title: 'Bem-vindo à área de Automação',
    intro:
      'Aqui ficam os recursos que ajudam a atender e a se comunicar com os clientes no WhatsApp. Cada um tem uma finalidade própria e você não precisa usar todos ao mesmo tempo. Para a maioria das empresas, o ponto de partida é um Agente de IA atendendo um número.',
    flow: [
      'Configurar o atendimento',
      'Definir instruções e recursos',
      'Vincular o número de WhatsApp',
      'Testar no Simulador',
      'Ativar e acompanhar'
    ],
    blocks: [
      {
        icon: 'cpu',
        title: 'Atendimento automático',
        items: [
          {
            text: 'Agente de IA: entende a mensagem do cliente e responde seguindo suas instruções.',
            permission: AGENTS.permission
          },
          {
            text: 'Chatbots: fluxos de atendimento que podem ser vinculados a um número.',
            permission: CHATBOTS.permission
          }
        ]
      },
      {
        icon: 'message-square',
        title: 'Agilidade no atendimento humano',
        items: [
          {
            text: 'Templates: modelos de mensagem reutilizáveis.',
            permission: TEMPLATES.permission
          },
          {
            text: 'Respostas Rápidas: textos prontos com atalho, para o atendente inserir.',
            permission: QUICK_REPLIES.permission
          }
        ]
      },
      {
        icon: 'send',
        title: 'Comunicação planejada',
        items: [
          {
            text: 'Campanhas: envio de mensagem para um segmento de leads, com o agente respondendo quem retornar.',
            permission: CAMPAIGNS.permission
          },
          {
            text: 'Automações: regras de mensagem por evento, como boas-vindas e lembretes.',
            permission: AUTOMATIONS.permission
          }
        ]
      }
    ]
  },
  {
    id: 'agents',
    permission: AGENTS.permission,
    short: 'Agentes de IA',
    icon: 'cpu',
    title: 'Agentes de IA',
    intro:
      'Um Agente de IA lê as mensagens que chegam no número vinculado a ele e responde em linguagem natural, seguindo as instruções que você escreve. Ele só executa as ações que você liberar.',
    blocks: [
      {
        icon: 'edit-2',
        title: 'Como ele é configurado',
        items: [
          'Aba Geral: nome, provedor e modelo de IA e o campo "Instruções do agente".',
          'Aba Intenções: os assuntos que ele deve reconhecer, com exemplos de frases.',
          'Aba Ações: o que ele pode fazer, como "Consultar catálogo", "Calcular orçamento", "Criar pedido" ou "Transferir para atendente".',
          'Aba Fluxos e canais: os números de WhatsApp que ele atende.'
        ]
      },
      {
        icon: 'shield',
        title: 'Limites e segurança',
        items: [
          'Cada ação pode exigir a confirmação do cliente antes de rodar.',
          'Cobranças só ficam disponíveis com pagamentos habilitados na aba Geral.',
          'Quando o atendente assume a conversa, o agente para de responder até ser devolvido.',
          '"Mensagem ao transferir para atendente" é o texto enviado ao passar a conversa para uma pessoa.'
        ]
      },
      {
        icon: 'play',
        title: 'Testar, ativar e acompanhar',
        items: [
          'Simulador: converse com o agente sem falar com clientes reais.',
          'Ativar / Desativar: controla se ele atende os números vinculados.',
          'Execuções: histórico de cada atendimento, com as ações feitas e as falhas.'
        ]
      }
    ],
    example:
      'Uma empresa recebe perguntas sobre produtos, preços e horários. O agente consulta o catálogo cadastrado, responde dentro das regras que você definiu e, quando não consegue resolver, transfere a conversa para um atendente.',
    links: [AGENTS]
  },
  {
    id: 'chatbots',
    permission: CHATBOTS.permission,
    short: 'Chatbots',
    icon: 'git-branch',
    title: 'Chatbots',
    intro:
      'Chatbots são fluxos de atendimento formados por etapas, como gatilho, mensagem, condição, espera, encaminhar para atendente e agente de IA. Enquanto o agente interpreta o que o cliente escreve, o fluxo segue um caminho definido.',
    blocks: [
      {
        icon: 'check-circle',
        title: 'O que já funciona',
        items: [
          'Listar, duplicar, ativar (publicar) e desativar fluxos.',
          'Vincular um fluxo publicado a um número em "Fluxo de atendimento", na tela de Números.',
          'Um fluxo pode passar a conversa para o Agente de IA.'
        ]
      },
      {
        icon: 'link-2',
        title: 'Como se relaciona com o Agente de IA',
        items: [
          'Cada número é atendido por um agente OU por um chatbot, nunca pelos dois.',
          'No agente, em "Fluxos e canais" › "Fluxos autorizados", você escolhe quais fluxos ele pode iniciar.'
        ]
      },
      {
        icon: 'alert-circle',
        title: 'Atenção',
        items: [
          'O editor de fluxos ainda só mostra as etapas: não dá para criar ou editar etapas pela tela.'
        ]
      }
    ],
    links: [CHATBOTS]
  },
  {
    id: 'automations',
    permission: AUTOMATIONS.permission,
    short: 'Automações',
    icon: 'zap',
    title: 'Automações',
    intro:
      'Automações são regras de mensagem ligadas a um momento da relação com o cliente, como boas-vindas, cobrança, pós-venda, lembrete ou aniversário. Elas são diferentes do agente: não interpretam conversas, apenas descrevem qual mensagem enviar em qual situação.',
    blocks: [
      {
        icon: 'check-circle',
        title: 'O que dá para fazer hoje',
        items: [
          'Ver as automações por categoria e filtrar entre Ativas e Inativas.',
          'Editar nome, categoria, gatilho e "Mensagem enviada".',
          'Ativar, desativar, duplicar e remover.'
        ]
      },
      {
        icon: 'alert-circle',
        title: 'Atenção',
        items: [
          'Ainda não há botão para criar uma automação nova.',
          'O gatilho é um texto descritivo e as regras ainda não são executadas automaticamente pela plataforma.'
        ]
      }
    ],
    links: [AUTOMATIONS]
  },
  {
    id: 'templates',
    permission: TEMPLATES.permission,
    short: 'Templates',
    icon: 'file-text',
    title: 'Templates de mensagem',
    intro:
      'Um template é um modelo de mensagem reutilizável, com cabeçalho e rodapé opcionais e variáveis como {{1}} e {{2}}. É diferente das instruções de um agente (regras de comportamento) e de um fluxo de chatbot (sequência de etapas).',
    blocks: [
      {
        icon: 'edit-2',
        title: 'Como criar',
        items: [
          'Clique em "Novo Template" e preencha "Nome do template", "Categoria" e "Corpo da mensagem".',
          'Informe as variáveis separadas por vírgula, se houver.'
        ]
      },
      {
        icon: 'check-circle',
        title: 'Como usar',
        items: [
          'Em Contatos, abra o contato, aba "Conversar", escolha "Usar template..." e clique em "Aplicar template".',
          'Templates aprovados pela Meta chegam pela sincronização da conta WhatsApp Business (conector AWS End User Messaging Social).'
        ]
      },
      {
        icon: 'alert-circle',
        title: 'Atenção',
        items: [
          'Templates criados aqui ficam como "Pendente": a tela não envia para aprovação da Meta.',
          'O agente de IA não usa templates.'
        ]
      }
    ],
    links: [TEMPLATES]
  },
  {
    id: 'quick-replies',
    permission: QUICK_REPLIES.permission,
    short: 'Respostas Rápidas',
    icon: 'zap-off',
    title: 'Respostas Rápidas',
    intro:
      'Respostas rápidas são textos prontos para o atendente humano não digitar sempre a mesma coisa. Cada uma tem um atalho que começa com "/", um título e uma categoria.',
    blocks: [
      {
        icon: 'edit-2',
        title: 'Como cadastrar',
        items: [
          'Clique em "Nova Resposta Rápida" e preencha "Atalho" (ex.: /horario), "Título", "Categoria" e "Texto da resposta".'
        ]
      },
      {
        icon: 'check-circle',
        title: 'Como usar',
        items: [
          'Em Contatos, abra o contato, aba "Conversar", e escolha "Inserir resposta rápida...".'
        ]
      },
      {
        icon: 'info',
        title: 'Quando usar',
        items: [
          'Respostas curtas e frequentes do atendimento humano.',
          'Para responder sozinho, sem atendente, use um Agente de IA: ele não usa as respostas rápidas.'
        ]
      }
    ],
    links: [QUICK_REPLIES]
  },
  {
    id: 'campaigns',
    permission: CAMPAIGNS.permission,
    short: 'Campanhas',
    icon: 'send',
    title: 'Campanhas',
    intro:
      'Campanhas são comunicações planejadas para um grupo de clientes, como promoções, avisos ou pesquisas. São diferentes do agente, que responde quem já está conversando com você.',
    blocks: [
      {
        icon: 'edit-2',
        title: 'Como cadastrar',
        items: [
          'Clique em "Nova Campanha" e escolha a segmentação de leads, o agente de IA que vai responder, o número de envio e a mensagem.',
          'Abra a campanha para ver os requisitos, iniciar ou agendar o envio e acompanhar o log de cada destinatário e o tempo restante.',
          'Na lista você pausa, retoma, duplica e gera relatórios.'
        ]
      },
      {
        icon: 'alert-circle',
        title: 'Atenção',
        items: [
          'Só recebem os leads com consentimento registrado, WhatsApp válido e sem descadastro.',
          'No número oficial (AWS), a primeira mensagem precisa ser um template aprovado pela Meta.',
          'Quem responder é atendido pelo agente de IA vinculado ao número.'
        ]
      }
    ],
    links: [CAMPAIGNS]
  },
  {
    id: 'comparison',
    short: 'Comparativo',
    icon: 'grid',
    title: 'Como tudo se relaciona',
    intro:
      'São ferramentas complementares. Este resumo mostra o papel de cada uma e o que dá para fazer com ela hoje.',
    comparison: [
      {
        icon: 'cpu',
        title: 'Agente de IA',
        purpose: 'Atender clientes com respostas em linguagem natural.',
        when: 'Perguntas variadas, orçamento, pedido e cobrança.',
        how: 'Instruções, intenções e ações liberadas; atende os números vinculados.',
        relation: 'Pode iniciar chatbots autorizados; o número é exclusivo dele.',
        link: AGENTS
      },
      {
        icon: 'git-branch',
        title: 'Chatbots',
        purpose: 'Conduzir o cliente por um caminho fixo.',
        when: 'Menus e roteiros iguais para todos.',
        how: 'Fluxos publicados vinculados a um número.',
        relation: 'Pode passar a conversa ao agente; um número tem agente ou chatbot.',
        link: CHATBOTS
      },
      {
        icon: 'zap',
        title: 'Automações',
        purpose: 'Descrever mensagens por momento do cliente.',
        when: 'Boas-vindas, lembretes, pós-venda.',
        how: 'Regras com categoria, gatilho e mensagem; ainda não executadas.',
        relation: 'Independente dos demais recursos hoje.',
        link: AUTOMATIONS
      },
      {
        icon: 'send',
        title: 'Campanhas',
        purpose: 'Planejar comunicações para grupos.',
        when: 'Promoções, avisos e pesquisas.',
        how: 'Segmentação de leads, número de envio, mensagem ou template aprovado e intervalo.',
        relation: 'Usa templates aprovados no número oficial; o agente responde quem retornar.',
        link: CAMPAIGNS
      },
      {
        icon: 'file-text',
        title: 'Templates',
        purpose: 'Padronizar mensagens reutilizáveis.',
        when: 'Textos oficiais com variáveis.',
        how: 'Corpo, cabeçalho, rodapé e variáveis; aprovados vêm do WhatsApp Business.',
        relation: 'Usados pelo atendente na aba "Conversar" do contato.',
        link: TEMPLATES
      },
      {
        icon: 'zap-off',
        title: 'Respostas Rápidas',
        purpose: 'Agilizar o atendente humano.',
        when: 'Respostas curtas e repetidas.',
        how: 'Atalho com "/", título, categoria e texto.',
        relation: 'Usadas na aba "Conversar" do contato; o agente não usa.',
        link: QUICK_REPLIES
      }
    ]
  },
  {
    id: 'first-agent',
    permission: 'ai_agent.create',
    short: 'Primeiro agente',
    icon: 'play',
    title: 'Crie seu primeiro Agente de IA',
    intro:
      'Siga estes passos na tela de Agente de IA. Os nomes abaixo são os mesmos dos campos e botões da plataforma.',
    tutorial: [
      {
        title: 'Acesse Agente de IA',
        text: 'No menu Automação, clique em "Agente de IA". Se a aba "Provedores de IA" não tiver um provedor ativo, configure-o primeiro: sem isso o agente não é criado.'
      },
      {
        title: 'Clique em "Novo agente"',
        text: 'Em "Tipo de execução", escolha "Nativo (CashPago)" para usar todos os recursos desta tela.'
      },
      {
        title: 'Defina a identidade',
        text: 'Preencha "Nome" (obrigatório) e "Descrição", e escolha "Provedor" e "Modelo".'
      },
      {
        title: 'Escreva as instruções',
        text: 'Em "Instruções do agente", descreva o papel, o tom de voz, os objetivos, o que ele não pode fazer e quando deve transferir para um atendente. Ajuste "Mensagem ao transferir para atendente".'
      },
      {
        title: 'Dê as informações de referência',
        text: 'O agente consulta o catálogo de Produtos e Serviços. Mantenha itens, preços e agenda atualizados e libere "Consultar catálogo" na aba Ações. Use a aba Intenções para os assuntos mais comuns.'
      },
      {
        title: 'Clique em "Criar agente"',
        text: 'Depois de criado, revise as abas Ações (o que ele pode fazer) e Fluxos e canais, onde você escolhe o número em "Números atendidos" e clica em "Vincular".'
      },
      {
        title: 'Teste no Simulador',
        text: 'Na aba Simulador, escreva como se fosse um cliente. Catálogo e orçamento são consultados de verdade; pedidos, cobranças e transferências são apenas simulados.'
      },
      {
        title: 'Ative e acompanhe',
        text: 'Use "Ativar" na lista de agentes. Acompanhe cada atendimento na aba Execuções e o histórico completo em Auditoria.'
      }
    ],
    showInstructionExample: true,
    action: { state: 'app.ai-agents', params: { acao: 'novo' } }
  }
];
